import {
  useQuery,
  useMutation,
  useQueryClient,
  UseMutationOptions,
  UseQueryOptions,
  UseQueryResult,
  UseMutationResult,
} from 'react-query'
import { SitesApi as LegacyAuthSitesApi } from '@trybeapp/sdk.auth'
import {
  ListSitePmsIntegrations,
  ListSites,
  ListSitesRequest,
  ResponseError,
  ShowSiteCategories,
  ShowSiteLogo,
  SiteFlagsResponse,
  SiteResponse,
  SitesApi,
} from '@trybeapp/sdk'
import { SitesApi as LegacyShopSitesApi } from '@trybeapp/sdk.shop'
import { usePageQueryParam } from 'hooks/UsePageQueryParam'
import { useCurrentSiteId } from 'contexts/SiteConfig'
import { apiConfiguration } from 'utilities/TrybeApiClient'

const legacyAuthSitesApi = new LegacyAuthSitesApi()
const legacyShopSitesApi = new LegacyShopSitesApi()
const sitesApi = new SitesApi(apiConfiguration)

export const useSites = (
  params: ListSitesRequest = {},
  options: UseQueryOptions<ListSites, ResponseError> = {}
) => {
  return useQuery<ListSites, ResponseError>(
    ['sites', params],
    () => sitesApi.listSites(params),
    options
  )
}

export const usePagedSites = (params = {}) => {
  const [page] = usePageQueryParam()

  return useSites({ page, ...params })
}

export const useGetSite = (siteId, options = {}) => {
  return useQuery(['sites', siteId], () => legacyAuthSitesApi.siteShow(siteId), options)
}

export const useAddSite = (options: UseMutationOptions = {}) => {
  const queryClient = useQueryClient()

  return useMutation(async (site) => await legacyAuthSitesApi.siteStore(site), {
    ...options,
    onSuccess: (result, variables, context) => {
      queryClient.invalidateQueries(['sites'])
      queryClient.invalidateQueries(['organisations'])

      if (options.onSuccess) {
        options.onSuccess(result, variables, context)
      }
    },
  })
}

export const useSiteLogo = (options: UseQueryOptions<ShowSiteLogo, ResponseError> = {}) => {
  const siteId = useCurrentSiteId()

  return useQuery<ShowSiteLogo, ResponseError>(
    ['siteLogo', { siteId }],
    () => sitesApi.getSiteLogo({ siteId }),
    options
  )
}

export const useUpdateSiteLogo = () => {
  const siteId = useCurrentSiteId()
  const queryClient = useQueryClient()

  return useMutation<SiteResponse, ResponseError, string>(
    (mediaId) =>
      sitesApi.updateSite({
        siteId: siteId,
        updateSite: {
          logo_media_id: mediaId,
        },
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['siteLogo', { siteId }])
      },
    }
  )
}

export const useUpdateSite = (options: UseMutationOptions = {}) => {
  const queryClient = useQueryClient()

  return useMutation(
    async (args: any) => {
      const [siteId, site] = args
      return await legacyAuthSitesApi.siteUpdate(siteId, site)
    },
    {
      ...options,
      onSuccess: (result, variables, context) => {
        queryClient.invalidateQueries('sites')
        if (options.onSuccess) {
          options.onSuccess(result, variables, context)
        }
      },
    }
  )
}

export const useDeleteSite = (options: UseMutationOptions = {}) => {
  const queryClient = useQueryClient()

  return useMutation(async (siteId) => await legacyAuthSitesApi.siteDestroy(siteId), {
    ...options,
    onSuccess: (result, variables, context) => {
      queryClient.invalidateQueries('sites')
      if (options.onSuccess) {
        options.onSuccess(result, variables, context)
      }
    },
  })
}

export const useGetSiteStripeDashboardLink = (
  siteId: string,
  options?: UseQueryOptions
): UseQueryResult<any> => {
  return useQuery(
    ['siteDashboard', siteId, options],
    async () => await legacyAuthSitesApi.siteStripeDashboard(siteId),
    options
  )
}

export const useGetPmsIntegrations = (
  options: UseQueryOptions<ListSitePmsIntegrations, ResponseError> = {}
) => {
  const siteId = useCurrentSiteId()

  options.cacheTime = options.cacheTime ?? Infinity
  options.staleTime = options.staleTime ?? 60 * 60 * 1000 // 60 minutes
  options.refetchOnMount = options.refetchOnMount ?? false

  return useQuery<ListSitePmsIntegrations, ResponseError>(
    ['pmsIntegrations', siteId],
    () => sitesApi.listPmsIntegrations({ siteId }),
    options
  )
}

export const useGetSoldByUsers = (options: UseQueryOptions = {}): UseQueryResult<any> => {
  const siteId = useCurrentSiteId()
  options.cacheTime = options.cacheTime ?? Infinity
  options.staleTime = options.staleTime ?? 60 * 60 * 1000 // 60 minutes

  return useQuery(
    ['soldByUsers', siteId],
    async () => await legacyShopSitesApi.siteSoldByUsersIndex(siteId),
    options
  )
}

export const useFlagsForSite = (siteId: string, options?: UseQueryOptions<SiteFlagsResponse>) => {
  return useQuery(['siteFeatureFlags', siteId], () => sitesApi.getSiteFlags({ siteId }), options)
}

export const useGetSiteCategories = (
  options: UseQueryOptions<ShowSiteCategories, ResponseError> = {}
) => {
  const siteId = useCurrentSiteId()

  return useQuery<ShowSiteCategories, ResponseError>(
    ['siteCategories', siteId],
    () => sitesApi.getSiteCategories({ siteId }),
    options
  )
}
export const useGetSiteVoucherCategories = (
  options: UseQueryOptions<ShowSiteCategories, ResponseError> = {}
) => {
  const siteId = useCurrentSiteId()

  const voucherApplicable = true

  return useQuery<ShowSiteCategories, ResponseError>(
    ['siteVoucherCategories', siteId],
    () => sitesApi.getSiteCategories({ siteId, voucherApplicable }),
    options
  )
}

export const useGetSiteSupportedCommsLocales = (siteId: string) =>
  useQuery(['supportedCommsLocales', { siteId }], () =>
    sitesApi.getSupportedCommsLocales({ siteId })
  )

export const useUpdateSiteSupportedCommsLocales = (siteId: string) => {
  const queryClient = useQueryClient()

  return useMutation(
    (locales: string[]) =>
      sitesApi.updateSupportedCommsLocales({
        siteId,
        updateSupportedCommsLocales: {
          locales,
        },
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['supportedCommsLocales', { siteId }])
      },
    }
  )
}

export const useCheckSiteEmailFromAddressVerificationStatus = (): UseMutationResult => {
  const siteId = useCurrentSiteId()

  return useMutation(() =>
    sitesApi.actionCheckEmailFromAddressVerification({
      siteId,
    })
  )
}
